import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, ShieldCheck, Users, Lock, Edit2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';

const pretty = (k) => k.replace(/^(msg|org)_/, '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
// Presets the owner may customize (Owner/Co-owner stay fully locked).
const EDITABLE_SYSTEM = ['Chatter', 'Manager chatter'];

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get('/roles'), api.get('/permissions')]);
      setRoles(r.data);
      setCategories(p.data.categories || {});
    } catch (e) { toast.error(apiError(e, 'Impossible de charger les rôles')); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/roles/${id}`);
      setRoles(roles.filter(x => x.id !== id));
      toast.success('Rôle supprimé');
    } catch (e) { toast.error(apiError(e, 'Échec de la suppression')); }
  };

  const totalPerms = Object.values(categories).reduce((s, v) => s + v.length, 0);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="roles-page">
      <PageHeader
        title="Rôles et permissions"
        subtitle={`${totalPerms} permissions granulaires sur ${Object.keys(categories).length} domaines — vérifiées côté serveur`}
        actions={<RoleDialog categories={categories} editing={editing} setEditing={setEditing} onSaved={load} />}
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {roles.map(r => (
          <div key={r.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-indigo-500/30 transition-colors" data-testid={`role-card-${r.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center"><ShieldCheck size={17} className="text-indigo-400" /></div>
              {r.system ? (
                EDITABLE_SYSTEM.includes(r.name) ? (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]">Système</Badge>
                    <Button data-testid={`edit-role-${r.id}`} variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:bg-white/5" onClick={() => setEditing(r)}><Edit2 size={12} /></Button>
                  </div>
                ) : (
                  <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]"><Lock size={9} className="mr-1" /> Système</Badge>
                )
              ) : (
                <div className="flex gap-1">
                  <Button data-testid={`edit-role-${r.id}`} variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:bg-white/5" onClick={() => setEditing(r)}><Edit2 size={12} /></Button>
                  <Button data-testid={`delete-role-${r.id}`} variant="ghost" size="icon" className="h-7 w-7 text-rose-400 hover:bg-rose-500/10" onClick={() => remove(r.id)}><Trash2 size={12} /></Button>
                </div>
              )}
            </div>
            <div className="text-base font-semibold text-white mb-1">{r.name}</div>
            <div className="text-xs text-slate-400 mb-3 min-h-[32px]">{r.description}</div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 pt-3 border-t border-white/5">
              <span className="flex items-center gap-1"><Users size={10} /> {r.member_count} membres</span>
              <span className="text-indigo-300">{r.permissions.length} permissions</span>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-base font-semibold text-white mb-3">Matrice des permissions</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(categories).map(([cat, perms]) => (
          <div key={cat} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-white capitalize">{cat}</div>
              <Badge variant="outline" className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-[10px]">{perms.length}</Badge>
            </div>
            <ul className="space-y-1.5">
              {perms.map(p => (
                <li key={p} className="text-xs text-slate-400 flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-indigo-400 shrink-0" /> {pretty(p)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoleDialog({ categories, editing, setEditing, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;

  useEffect(() => {
    if (editing) {
      setForm({ name: editing.name, description: editing.description || '', permissions: editing.permissions || [] });
      setOpen(true);
    }
  }, [editing]);

  const close = (v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: '', description: '', permissions: [] }); } };

  const toggle = (p) => setForm(f => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p] }));

  const save = async () => {
    if (!form.name) { toast.error('Nom du rôle requis'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/roles/${editing.id}`, form);
        toast.success('Rôle mis à jour');
      } else {
        await api.post('/roles', form);
        toast.success('Rôle créé');
      }
      close(false);
      onSaved();
    } catch (e) { toast.error(apiError(e, "Échec de l'enregistrement")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild><Button data-testid="create-role-button" className="bg-indigo-500 hover:bg-indigo-600"><Plus size={16} className="mr-1.5" /> Créer un rôle</Button></DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle className="text-white">{isEdit ? `Modifier le rôle — ${editing?.name}` : 'Créer un rôle personnalisé'}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Nom du rôle {isEdit && editing?.system && <span className="text-slate-500">(verrouillé)</span>}</Label><Input data-testid="role-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} disabled={isEdit && editing?.system} className="bg-white/5 border-white/10 text-white h-10 disabled:opacity-60" /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Description</Label><Input data-testid="role-description-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
          </div>
          <div className="text-xs text-slate-400">{form.permissions.length} permissions sélectionnées</div>
          <div className="space-y-4">
            {Object.entries(categories).map(([cat, perms]) => (
              <div key={cat}>
                <div className="text-xs font-semibold text-white capitalize mb-2">{cat}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {perms.map(p => (
                    <label key={p} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-white/5">
                      <Checkbox data-testid={`perm-checkbox-${p}`} checked={form.permissions.includes(p)} onCheckedChange={() => toggle(p)} />
                      {pretty(p)}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="role-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : isEdit ? 'Enregistrer les changements' : 'Créer le rôle'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
